import useEmitter, { EmitterEvents } from "@/hooks/useEmitter";
import useSocketService from "@/hooks/useSocketService";
import { getCurrentInstance } from "vue";

let lastNotification = -Infinity;

const showConnectionError = (): void => {
	if (Date.now() - lastNotification < 2_000) return;
	lastNotification = Date.now();
	useEmitter().emit(EmitterEvents.GameShowConnectionErrorPopup, {
		recoverable: true,
	});
};

const isConnected = (): boolean =>
	navigator.onLine && useSocketService().isSocketActive;

const requireConnection = (silent = false): boolean => {
	if (isConnected()) return true;
	if (!silent) showConnectionError();
	return false;
};

const checkConnection = async (silent = false): Promise<boolean> => {
	if (!requireConnection(silent)) return false;
	try {
		return (
			!(await useSocketService().emitStateRequest()) &&
			requireConnection(silent)
		);
	} catch {
		if (!silent) showConnectionError();
		return false;
	}
};

type GuardedAction = () => void | Promise<void>;

type RewardClaimGuard = {
	requireConnection: (silent?: boolean) => boolean;
	checkConnection: (silent?: boolean) => Promise<boolean>;
	guard: (action: GuardedAction, isActive?: () => boolean) => Promise<void>;
};

export default function useRewardClaimGuard(): RewardClaimGuard {
	const component = getCurrentInstance();
	let running = false;

	const guard = async (
		action: GuardedAction,
		isActive?: () => boolean,
	): Promise<void> => {
		if (running) return;
		running = true;

		try {
			if (!(await checkConnection())) return;
			if (isActive ? !isActive() : component?.isUnmounted) return;
			await action();
		} catch (error: unknown) {
			console.error("Reward action could not finish", error);
			showConnectionError();
		} finally {
			running = false;
		}
	};

	return { requireConnection, checkConnection, guard };
}
